import { css } from '@emotion/react';
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import t from 'react-translate';
import { tripleSpacing } from 'styles/global_defaults/scaffolding';
import { isEmpty, pluck } from 'underscore';
import { AngularServicesContext } from 'react-app';
import { useFormContext } from 'react-hook-form';

// redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { fetchCollectionsList } from 'redux/actions/collections';
import { convertToCollectionLesson } from 'redux/actions/lecture-pages';
import { SyncingStatus } from 'redux/schemas/models/lecture-page';

// components
import { Button } from 'react-bootstrap';
import useInfiniteScroll from 'shared/hooks/use-infinite-scroll';
import LoadingPlaceholder, { LoaderType } from 'shared/components/loading-placeholder';
import { collectionSortKeys } from 'content-library/components/collection-list';
import { closeConfirmationDialog, openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import CollectionRow from './collection-row';
import { PAGE_SIZE } from '../course-lesson-link/link-lessons-modal-body';

export enum CollectionListModalType {
  LESSON_LINK = 'lesson link',
  LESSON_TRANSFER = 'lesson transfer',
  FOLDER_TRANSFER = 'folder transfer',
  BULK_LINK = 'bulk link',
}

export enum ModalActionType {
  LINK = 'link',
  COPY = 'copy',
  MOVE = 'move',
}

type CollectionListModalBodyProps = {
  lessonId?: number
  onClose?: (isConverted?: boolean) => void
  bodyDescription?: string
  collectionListModalType?: CollectionListModalType
  modalActionType?: ModalActionType
  submitAction?: Function
  currentCatalogId?: string
  className?: string;
};

const styles = css`
  .content {
    // 370px = Modal padding(100) + Modal header(60) + Description(70) + Button with spacing(140)
    max-height: calc(100vh - 370px);
    overflow-y: auto;
  }
  .button-container {
    margin-top: ${tripleSpacing}px;
  }
`;
type InitialStateType = {
  selectedFolderId?: number
  setSelectedFolderId?: Function
  selectedCollectionName?: string
  setSelectedCollectionName?: Function
  collectionListModalType?: CollectionListModalType
  modalActionType?: ModalActionType
};

const initialState: InitialStateType = {
  selectedFolderId: null,
  setSelectedFolderId: (selectedFolderId: number) => {},
  selectedCollectionName: '',
  setSelectedCollectionName: (name: string) => {},
  collectionListModalType: null,
  modalActionType: null,
};
export const CollectionListModalContext = createContext(initialState);

type CollectionListSubmitButtonProps = {
  modalActionType: ModalActionType
  onSubmit: () => void
  saving: boolean
  selectedFolderId?: number
  collectionListModalType?: CollectionListModalType
};
const CollectionListSubmitButton = (props: CollectionListSubmitButtonProps) => {
  const { modalActionType, onSubmit, saving, collectionListModalType } = props;
  const { watch } = useFormContext() || {};

  const getSelectedFolderId = () => {
    switch (collectionListModalType) {
      case CollectionListModalType.LESSON_LINK:
        return props.selectedFolderId;
      case CollectionListModalType.BULK_LINK:
        return null;
      default:
        return watch('destinationFolderId');
    }
  };

  const selectedCatalogId = watch?.('destinationCatalogId');

  const selectedCollection = useSelector((state) => state?.models?.courses[selectedCatalogId]);
  const foldersCount = selectedCollection?.foldersCount || 0;

  const isDisabled = () => {
    switch (collectionListModalType) {
      case CollectionListModalType.LESSON_LINK:
        return !getSelectedFolderId();
      case CollectionListModalType.BULK_LINK:
        return !selectedCatalogId;
      case CollectionListModalType.FOLDER_TRANSFER:
        return !selectedCatalogId;
      case CollectionListModalType.LESSON_TRANSFER:
        if (foldersCount === 0) {
          return !selectedCatalogId;
        }
        return !getSelectedFolderId();
      default:
        return false;
    }
  };

  switch (modalActionType) {
    case ModalActionType.LINK:
      return (
        <Button
          className='ml-2 font-weight-bold'
          variant='primary'
          onClick={onSubmit}
          disabled={isDisabled() || saving}
        >
          {t.FORM[saving ? 'ADDING' : 'ADD']()}
        </Button>
      );
    case ModalActionType.COPY:
      return (
        <Button
          className='ml-2 font-weight-bold'
          variant='primary'
          onClick={onSubmit}
          disabled={saving || isDisabled()}
        >
          {t.FORM.COPY()}
        </Button>
      );
    case ModalActionType.MOVE:
      return (
        <Button
          className='ml-2 font-weight-bold'
          variant='primary'
          onClick={onSubmit}
          disabled={saving || isDisabled()}
        >
          {t.FORM.MOVE()}
        </Button>
      );
    default:
      return null;
  }
};

const CollectionListModalBody = (props: CollectionListModalBodyProps) => {
  const hasMore = useSelector((state) => state.app.collection.hasMoreInCollectionSearch);

  const {
    bodyDescription,
    collectionListModalType,
    modalActionType,
    submitAction,
    className,
  } = props;

  const dispatch = useAppDispatch();

  const [saving, setSaving] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [collectionCatalogIds, setCollectionCatalogIds] = useState<string[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState(initialState.selectedFolderId);
  const [selectedCollectionName, setSelectedCollectionName] = useState(initialState.selectedCollectionName);

  const { TimelinesManager, $timeout } = useContext(AngularServicesContext);
  const scrollingContentRef = useRef<HTMLDivElement>(null);
  const SCROLL_PADDING = 20;
  const isScrolledToBottom = useInfiniteScroll(
    scrollingContentRef.current,
    SCROLL_PADDING,
  );

  // For infinite scrolling
  useEffect(() => {
    if (isScrolledToBottom && hasMore && !loading) {
      setLoading(true);
      setPageNo(pageNo + 1);
    }
  }, [hasMore, isScrolledToBottom, pageNo, loading]);

  const onFetchCollections = useCallback((): Promise<any> => new Promise((resolve) => {
    const payload = {
      pageIndex: pageNo,
      pageSize: PAGE_SIZE,
      sorting: [`${collectionSortKeys[0]}&order=asc`],
      includeBlank: collectionListModalType !== CollectionListModalType.LESSON_LINK,
      editableOnly: true,
    };

    dispatch(fetchCollectionsList(payload)).then((res) => {
      if (!isEmpty(res?.error)) {
        dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.ERROR_SOMETHING_WRONG(),
        }));
        resolve([]);
      }

      resolve(pluck(res.payload.response ?? [], 'catalogId'));
    });
  }), [dispatch, pageNo]);

  useEffect(() => {
    if (loading) {
      onFetchCollections()
        .then((response) => {
          setLoading(false);
          setIsLoaded(true);
          setCollectionCatalogIds((prevList) => ([...prevList, ...response ?? []]));
          if (collectionListModalType === CollectionListModalType.FOLDER_TRANSFER) {
            setCollectionCatalogIds((prevList) => {
              const newList = [...prevList];
              return newList.filter((catalogId) => catalogId !== props.currentCatalogId);
            });
          }
        });
    }
  }, [onFetchCollections, loading, collectionListModalType, props.currentCatalogId]);

  const onSubmit = () => {
    setSaving(true);

    const payload = {
      lecturePageId: props.lessonId,
      folderId: selectedFolderId,
    };
    switch (collectionListModalType) {
      case CollectionListModalType.LESSON_LINK:
        dispatch(convertToCollectionLesson(payload)).then((response) => {
          if (isEmpty(response?.error)) {
            TimelinesManager.updateLecturePageAttribute(props.lessonId, 'isLinked', true);
            TimelinesManager.updateLecturePageAttribute(props.lessonId, 'syncingStatus', SyncingStatus.CONVERT_IN_PROGRESS);
            TimelinesManager.refreshConvertibleLectureCounts();

            if (response?.payload?.externalExercises?.length !== 0) {
              dispatch(
                openConfirmationDialog({
                  icon: 'success',
                  title: t.LECTURE_PAGES.COMPONENTS.SUBMISSIONS_DISCOVERY.ADD_FROM_COURSE_TO_LL.MODAL.TITLE(),
                  bodyText: t.LECTURE_PAGES.COMPONENTS.SUBMISSIONS_DISCOVERY.ADD_FROM_COURSE_TO_LL.MODAL.DESCRIPTION(),
                  confirmText: t.TIMELINE.ADD_TO_COLLECTION_WARNING_MODAL.GOT_IT(),
                  confirmButtonVariant: 'primary',
                  noCancelButton: true,
                  onConfirm: () => {
                    dispatch(closeConfirmationDialog());
                  },
                }),
              );
            }
            $timeout(() => {
              dispatch(addAlertMessage({
                type: AlertMessageType.SUCCESS,
                message: t.TIMELINE.ADD_TO_COLLECTION_MODAL.SUCCESS_ALERT_MSG(selectedCollectionName, response.payload.title),
              }));
            });
          }
          props.onClose(true);
        });
        break;
      case CollectionListModalType.LESSON_TRANSFER:
      case CollectionListModalType.FOLDER_TRANSFER:
      case CollectionListModalType.BULK_LINK:
        submitAction();
        break;
      default:
        break;
    }
  };

  return (
    <CollectionListModalContext.Provider
      value={{
        selectedFolderId,
        setSelectedFolderId,
        selectedCollectionName,
        setSelectedCollectionName,
        collectionListModalType,
        modalActionType,
      }}
    >
      <div css={styles} className={`mx-6 mt-5 ${className ?? ''}`}>
        <p className={`${collectionListModalType !== CollectionListModalType.LESSON_LINK ? 'text-gray-2' : ''} mb-0`}>
          {bodyDescription}
        </p>
        <div className='content' ref={scrollingContentRef}>
          {collectionCatalogIds?.map((catalogId) => (
            <CollectionRow
              key={catalogId}
              catalogId={catalogId}
            />
          ))}
          {loading && (
            <LoadingPlaceholder loaderType={LoaderType.HALF} className='p-4' />
          )}
          {isLoaded && !hasMore && collectionCatalogIds.length > 0 && (
            <p className='d-flex align-items-center gray-2 text-small mt-2'>
              {t.SEARCH.ALL_LOADED()}
            </p>
          )}
        </div>
        <div className='button-container d-flex justify-content-center mb-6'>
          <Button
            variant='secondary'
            className='font-weight-bold'
            onClick={() => props.onClose()}
          >
            {t.FORM.CANCEL()}
          </Button>
          <CollectionListSubmitButton
            onSubmit={onSubmit}
            selectedFolderId={selectedFolderId}
            modalActionType={modalActionType}
            collectionListModalType={collectionListModalType}
            saving={saving}
          />
        </div>
      </div>
    </CollectionListModalContext.Provider>
  );
};
export default CollectionListModalBody;
